
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { TreeNode } from "@/types/tree";
import { configStore, documentStore, uiStore } from "@/store/store";
import { getBlockFromWordPress } from "@/lib/global-blocks/global-blocks";
import { pipe } from "fp-ts/function";
import { fold } from "fp-ts/Either";
import { GlobalBlock } from "@/types/global-blocks";
import {
  calculateBackdropCutoutSize,
  startBackdropListeners
} from "@/lib/inline-editor/backdrop";
import {
  insertNodesInInlineBlock,
  pullNodesFromInlineBlock,
  updateBlockTree
} from "@/lib/inline-editor/inline-editor";
import { scrollToActiveElement } from "@/lib/canvas/scroll-to-active-element";
import { InlineEditingContext } from "@/types/inline-editor";

@Component({
  components: {
    BuilderElement: () => import("@/components/Canvas/BuilderElement.vue")
  }
})
export default class InlineEditor extends Vue {
  @Prop()
  element!: TreeNode;

  @Prop()
  ssrTemplate!: string | null;

  loading = false;
  saving = false;
  resizeObserver: ResizeObserver | null = null;
  blockId?: number | null = null;
  block?: GlobalBlock | null = null;
  nextNodeId?: number = 100;
  nodes: TreeNode[] = [];
  stopListeners: () => void = () => {};

  @Watch("context", { deep: true })
  onContextChange(newContext: InlineEditingContext) {
    if (newContext.nextNodeId) {
      this.nextNodeId = newContext.nextNodeId;
    }
  }

  @Watch("element", { deep: true })
  // @ts-ignore
  onElementChange() {
    this.$nextTick(() => {
      calculateBackdropCutoutSize(this.$refs.container as HTMLElement);
    });
  }

  get postId() {
    return documentStore.document.documentMeta.id;
  }

  get selector() {
    return `[data-inline-editable-node="${this.postId}-${this.element.id}"]`;
  }

  get parsedSsrTemplate() {
    if (!this.ssrTemplate) return null;

    const parser = new DOMParser();
    return parser.parseFromString(this.ssrTemplate, "text/html");
  }

  get ssrNodePlaceholders() {
    const elements = this.parsedSsrTemplate?.querySelectorAll(this.selector);

    if (!elements) return [];

    return Array.from(elements)
      .slice(1) // Ignore the first element
      .map(element => {
        return {
          tag: element.tagName.toLowerCase(),
          className: element.className || "",
          innerHTML: element.innerHTML
        };
      });
  }

  wrapEditorInSsrNode(editor: JSX.Element, placeholders: JSX.Element[] = []) {
    if (!this.ssrTemplate) return editor;

    const parser = new DOMParser();
    const doc = parser.parseFromString(this.ssrTemplate, "text/html");

    const getParentElements = (element: Element) => {
      const parents = [];
      let current: Element | null = element.parentElement;

      while (current && current.tagName.toLowerCase() !== "body") {
        parents.push({
          tag: current.tagName.toLowerCase(),
          className: current.className || ""
        });
        current = current.parentElement;
      }

      return parents;
    };

    const element = doc.querySelector(this.selector);

    if (!element) return editor;

    const parents = getParentElements(element);

    const Wrapper = element.tagName.toLowerCase();
    const WrappedItem = <Wrapper class={element.className}>{editor}</Wrapper>;

    // Reduce parents to nested JSX elements
    return parents.reverse().reduceRight(
      // @ts-ignore
      (child, parent) => {
        const Wrapper = parent.tag.toLowerCase();
        return <Wrapper class={parent.className}>{child}</Wrapper>;
      },
      [WrappedItem, ...placeholders]
    );
  }

  get context() {
    return uiStore.inlineEditingContext;
  }

  get blocks() {
    return configStore.globalBlocks;
  }

  async maybeLoadBlock() {
    const blockId = this.context?.blockId;

    if (!blockId) return;

    let block = this.blocks.find(block => block.id === blockId);

    if (!block) {
      block = await this.loadRemoteBlock(blockId);
    }

    this.blockId = blockId;
    this.block = block;
    this.nextNodeId = this.getCurrentNextNodeId();

    uiStore.setInlineEditingContext({
      ...uiStore.inlineEditingContext,
      nextNodeId: this.nextNodeId
    });

    insertNodesInInlineBlock(this.element.id, block!);
  }

  getCurrentNextNodeId() {
    return this.block?.tree !== false
      ? this.block?.tree._nextNodeId
      : undefined;
  }

  async loadRemoteBlock(blockId: number) {
    this.loading = true;

    return pipe(
      await getBlockFromWordPress(blockId),
      fold(
        () => {
          this.loading = false;
          return undefined;
        },
        block => {
          this.loading = false;
          return block;
        }
      )
    );
  }

  async saveBlock() {
    if (!this.block) return;

    const nodes = this.nodes.length
      ? this.nodes
      : pullNodesFromInlineBlock(this.element);

    if (!nodes.length) {
      console.log("No nodes found 🤔");
      return;
    }

    await updateBlockTree(this.block, this.element.id, nodes, this.nextNodeId);
  }

  beforePageSave() {
    // Keep a local copy of the nodes in case the elements are removed
    // from the DOM before the save is complete
    this.nodes = pullNodesFromInlineBlock(this.element);
    uiStore.resetInlineEditingContext();
  }

  async mounted() {
    const element = this.$refs.container as HTMLElement;
    this.stopListeners = startBackdropListeners(element);

    await this.maybeLoadBlock();

    this.$nextTick(() => scrollToActiveElement());

    document.addEventListener(
      "breakdanceSaveButtonClicked",
      this.beforePageSave
    );
  }

  async beforeDestroy() {
    await this.saveBlock();

    this.stopListeners();

    document.removeEventListener(
      "breakdanceSaveButtonClicked",
      this.beforePageSave
    );
  }

  render() {
    const loader = (
      <div class="breakdance-inline-editor__loader">Loading Block...</div>
    );

    const editor = (
      <div class="breakdance-inline-editor" ref="container">
        {this.loading ? loader : this.$slots.default}
      </div>
    );

    const placeholders = this.ssrNodePlaceholders.map((placeholder, i) => {
      const Wrapper = placeholder.tag;
      return (
        <Wrapper
          key={`placeholder-${i}`}
          class={`breakdance-inline-editor-placeholder ${placeholder.className}`}
          // @ts-ignore
          domPropsInnerHTML={placeholder.innerHTML}
        />
      );
    });

    if (this.ssrTemplate) {
      return this.wrapEditorInSsrNode(editor, placeholders);
    }

    return editor;
  }
}
